import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, ButtonBase, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, IconButton, InputAdornment,
    Link as MuiLink, MenuItem, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeOutlinedIcon          from '@mui/icons-material/HomeOutlined';
import CreditCardOutlinedIcon    from '@mui/icons-material/CreditCardOutlined';
import StorefrontOutlinedIcon    from '@mui/icons-material/StorefrontOutlined';
import BlockIcon                 from '@mui/icons-material/Block';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import HandshakeOutlinedIcon     from '@mui/icons-material/HandshakeOutlined';
import DeleteOutlineIcon         from '@mui/icons-material/DeleteOutline';
import AddIcon                   from '@mui/icons-material/Add';
import { ConfigCard }            from '../style/cards';
import { PdiRow }                from '../style/cards/_PdiHelpers';
import IconPicker                from './IconPicker';

// ────────────── Static option pools ──────────────
const POS_OPTIONS = [
    { value: '0199', label: '0199 — Webshop registrations' },
    { value: '0200', label: '0200 — Counter sales'         },
    { value: '0201', label: '0201 — B2B'                   },
];
const WAREHOUSE_OPTIONS = [
    { value: '01', label: '(01) Lochristi'    },
    { value: '02', label: '(02) Charleroi'    },
    { value: '03', label: '(03) Destelbergen' },
    { value: '04', label: '(04) softtouch'    },
];
// Fixed SoftTouch cheque/voucher types (platform constants, not per-customer).
// Labels come from i18n; the English text doubles as the fallback. Keys mirror
// the old admin's GLOBAL.CHEQUE_* terminology.
const DISCOUNT_TYPES = [
    { code: '999902', labelKey: 'cfg_proc_disc_type_gift_voucher',             label: 'Gift voucher',             defaultEnabled: true,  alwaysOn: false },
    { code: '999903', labelKey: 'cfg_proc_disc_type_deposit',                  label: 'Deposit',                  defaultEnabled: false, alwaysOn: false },
    { code: '999904', labelKey: 'cfg_proc_disc_type_voucher',                  label: 'Voucher',                  defaultEnabled: true,  alwaysOn: false },
    { code: '999905', labelKey: 'cfg_proc_disc_type_customer_discount',        label: 'Customer discount',        defaultEnabled: true,  alwaysOn: false },
    { code: '999907', labelKey: 'cfg_proc_disc_type_commercial_coupon',        label: 'Commercial coupon',        defaultEnabled: true,  alwaysOn: false },
    { code: '999908', labelKey: 'cfg_proc_disc_type_discount_type',            label: 'Discount type',            defaultEnabled: false, alwaysOn: false },
    { code: '999909', labelKey: 'cfg_proc_disc_type_customer_discount_coupon', label: 'Customer discount coupon', defaultEnabled: true,  alwaysOn: false },
];
const SALUTATIONS = [
    { value: '9001', label: '9001 — Mr.'       },
    { value: '9002', label: '9002 — Mrs.'      },
    { value: '9003', label: '9003 — Mx.'       },
    { value: '',     label: '— require choice —' },
];
const TICKET_TYPES = [
    { key: 'regular', label: 'Regular ticket', help: 'Standard receipt with line items' },
    { key: 'compact', label: 'Compact ticket', help: 'Smaller receipt with summary only' },
    { key: 'message', label: 'Webshop message', help: "Customer's notes, gift message, etc." },
];

// Default config — also the shape the parent's controlled state uses.
export const PROCESSING_CONFIG_DEFAULT = {
    // GENERAL
    access: {
        b2bOnly:         false,
        posCode:         '0199',
        guestCustomerId: '34',
        allowGuestPickup:false,
    },
    warehouse: '01',
    // Kasticket (receipt) POS IDs — which POS the receipt is processed on vs.
    // printed on. Legacy SuperUser tab; format "0101" (store 2 + pos 2).
    receiptPos: {
        process: '0101',  // PROCESS_RECEIPT_POS_ID_PROCESS
        print:   '0101',  // PROCESS_RECEIPT_POS_ID_RECEIPT
    },
    discountTypes: DISCOUNT_TYPES.reduce((acc, d) => {
        acc[d.code] = { enabled: d.defaultEnabled, acceptAsPayment: d.defaultEnabled, showInEmail: d.defaultEnabled };
        return acc;
    }, {}),
    discountBarcode: '',  // ITEM_DISCOUNT — barcode of the back-office discount product
    cart: {
        allowDiscountCodes:       true,
        allowNegativeTotals:      false,
        limitQuantityPerItem:     true,
        maxQuantityPerItem:       10,
        maxUniqueItemsInCart:     40,
    },
    registration: {
        phoneRequired:            true,
        defaultSalutation:        '9002',
        defaultCountry:           '',
        showPurchaseHistory:      true,
        showCustomerDiscount:     true,
    },
    externalStock: {
        iconName:  'truck-ramp-box',
        iconColor: '#f57c00',
    },
    ticketPrinting: {
        regular: { pickup: true,  shipped: true  },
        compact: { pickup: false, shipped: false },
        message: { pickup: true,  shipped: true  },
    },
    // PAYMENTS
    payments: {
        location:           'onlineAndStore',
        allowInvoice:       true,
        provider:           'msp',  // PAYMENT_PROVIDER — only MultiSafePay (Europabank/mpi is deprecated)
        paymentType:        '1',    // PROCESS_PAYMENT_TYPE (1-9)
    },
    // SHIPPING
    shipping: {
        method:                  'pickupAndShipping',
        routeExternalThroughShop:false,
        externalStockEnabled:    false,            // gated by Data sync
        defaultCost:             6,
        defaultPickupCost:       6,   // PICKUP_POINT_COST (parcel-point pickup)
        freeFromAmount:          99,
        shippingCostItem:        '17581401010101',
    },
    // CUSTOMER FIELDS are NOT settings — they're a CRUD list backed by
    // `customers_categories_groups`, owned by the parent (see customerCategoryGroups).
};

const ProcessingTab = ({ theme, t, subTab = 0, config: configProp, onConfigChange, posOptions = [], warehouseOptions = [], salutationOptions = [], countryOptions = [], paymentProviderAvailability = {}, paymentTypeOptions = [], customerCategoryGroups = [], onCustomerCategoryGroupsChange, stores = [], onStoresChange, validateCustomerId, onApplyCountryPreset }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    // Controlled by the parent (settings load+save) when wired; falls back to
    // local state so the tab still works standalone.
    const controlled = configProp != null && typeof onConfigChange === 'function';
    const [localConfig, setLocalConfig] = useState(PROCESSING_CONFIG_DEFAULT);
    const config = controlled ? configProp : localConfig;
    const setConfig = controlled ? onConfigChange : setLocalConfig;

    const setSection = (k) => (patch) => setConfig(p => ({ ...p, [k]: { ...(p[k] || {}), ...patch } }));

    return (
        <Box>
            {subTab === 0 && <GeneralPanel    config={config} setConfig={setConfig} setSection={setSection} posOptions={posOptions} warehouseOptions={warehouseOptions} salutationOptions={salutationOptions} countryOptions={countryOptions} validateCustomerId={validateCustomerId} theme={theme} tr={tr} />}
            {subTab === 1 && <PaymentsPanel   value={config.payments} onChange={setSection('payments')}    providerAvailability={paymentProviderAvailability} typeOptions={paymentTypeOptions} receiptPos={config.receiptPos} onReceiptPosChange={setSection('receiptPos')} posOptions={posOptions} theme={theme} tr={tr} />}
            {subTab === 2 && <ShippingPanel   value={config.shipping} onChange={setSection('shipping')}    stores={stores} onStoresChange={onStoresChange} onApplyCountryPreset={onApplyCountryPreset} posOptions={posOptions} theme={theme} tr={tr} />}
            {subTab === 3 && <CustomerFieldsPanel
                                value={customerCategoryGroups}
                                onChange={onCustomerCategoryGroupsChange}
                                theme={theme} tr={tr} />}
        </Box>
    );
};
ProcessingTab.propTypes = { theme: PropTypes.string, t: PropTypes.func, subTab: PropTypes.number, config: PropTypes.object, onConfigChange: PropTypes.func, posOptions: PropTypes.array, warehouseOptions: PropTypes.array, salutationOptions: PropTypes.array, countryOptions: PropTypes.array, paymentProviderAvailability: PropTypes.object, paymentTypeOptions: PropTypes.array, customerCategoryGroups: PropTypes.array, onCustomerCategoryGroupsChange: PropTypes.func, stores: PropTypes.array, onStoresChange: PropTypes.func, validateCustomerId: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// GENERAL panel — many cards
// ─────────────────────────────────────────────────────────────────────────
const GeneralPanel = ({ config, setConfig, setSection, posOptions = [], warehouseOptions = [], salutationOptions = [], countryOptions = [], validateCustomerId, theme, tr }) => {
    const setAccess       = setSection('access');
    const setCart         = setSection('cart');
    const setRegistration = setSection('registration');
    const setExternal     = setSection('externalStock');
    const setTicket       = (type, channel, val) => setConfig(p => ({
        ...p,
        ticketPrinting: {
            ...p.ticketPrinting,
            [type]: { ...(p.ticketPrinting[type] || {}), [channel]: val },
        },
    }));
    const setDiscount     = (code, key, val) => setConfig(p => ({
        ...p,
        discountTypes: {
            ...p.discountTypes,
            [code]: { ...(p.discountTypes[code] || {}), [key]: val },
        },
    }));

    const guestEnabled = !!config.access.guestCustomerId?.trim();

    // POS list comes from the POS backend (sftData type "pos") via the parent;
    // fall back to the static pool if it hasn't loaded. Keep the currently-saved
    // code selectable even if it's absent from the fetched list.
    const posList = (Array.isArray(posOptions) && posOptions.length) ? posOptions : POS_OPTIONS;
    const posListWithCurrent = (config.access.posCode && !posList.some(o => o.value === config.access.posCode))
        ? [{ value: config.access.posCode, label: config.access.posCode }, ...posList]
        : posList;

    // Warehouse/store list — same treatment as POS (sftData "stores" via parent).
    const whList = (Array.isArray(warehouseOptions) && warehouseOptions.length) ? warehouseOptions : WAREHOUSE_OPTIONS;
    const whListWithCurrent = (config.warehouse && !whList.some(o => o.value === config.warehouse))
        ? [{ value: config.warehouse, label: config.warehouse }, ...whList]
        : whList;

    // Salutation list — customer codes group 90 (sftData "customerCodes" via parent).
    // Always offer an empty "require choice" option; keep the saved value visible.
    const salBase = (Array.isArray(salutationOptions) && salutationOptions.length)
        ? salutationOptions
        : SALUTATIONS.filter(o => o.value);
    const salList = [{ value: '', label: tr('cfg_proc_reg_salutation_none', '— require choice —') }, ...salBase];
    const salListWithCurrent = (config.registration.defaultSalutation && !salList.some(o => o.value === config.registration.defaultSalutation))
        ? [...salList, { value: config.registration.defaultSalutation, label: config.registration.defaultSalutation }]
        : salList;

    // Country list — all webshop countries from the Data tab (sftData/proxy via
    // parent), already ordered prefered-first. No static fallback: an invalid
    // (name-based) value must never be savable, so when the list isn't loaded we
    // only keep the currently-saved iso visible.
    const countryBase = (Array.isArray(countryOptions) && countryOptions.length) ? countryOptions : [];
    const countryListWithCurrent = (config.registration.defaultCountry && !countryBase.some(o => o.value === config.registration.defaultCountry))
        ? [...countryBase, { value: config.registration.defaultCountry, label: config.registration.defaultCountry }]
        : countryBase;

    // Live validation of the guest customer id against the POS customer DB
    // (debounced; parent supplies the auth call). status: idle|checking|valid|invalid|error
    const [custCheck, setCustCheck] = useState({ status: 'idle' });
    const validateRef = useRef(validateCustomerId);
    validateRef.current = validateCustomerId;
    const guestIdValue = config.access.guestCustomerId;
    useEffect(() => {
        const id = String(guestIdValue || '').trim();
        if (!id || typeof validateRef.current !== 'function') { setCustCheck({ status: 'idle' }); return undefined; }
        let cancelled = false;
        setCustCheck({ status: 'checking' });
        const handle = setTimeout(() => {
            Promise.resolve(validateRef.current(id)).then((res) => {
                if (!cancelled) setCustCheck(res && res.status ? res : { status: 'idle' });
            });
        }, 450);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [guestIdValue]);

    return (
        <Stack spacing={2}>
            {/* Access & account creation */}
            <ConfigCard
                title={tr('cfg_proc_access_title', 'Access & account creation')}
                description={tr('cfg_proc_access_desc', 'Who can place orders, and how their customer accounts are set up in your system.')}
                theme={theme}
            >
                <Stack spacing={1.25} divider={<Divider />}>
                    {/* B2B-only mode — hidden for now. Not in use yet but may
                        come back later; left in place (commented out) so the
                        state plumbing + i18n keys don't have to be re-added. */}
                    {false && (
                        <PdiRow
                            label={tr('cfg_proc_b2b_only', 'B2B-only mode')}
                            help={tr('cfg_proc_b2b_only_help', "Restrict checkout to registered B2B customers only. Other visitors can still browse but can't order.")}
                            control={<Switch color="success" size="small"
                                checked={!!config.access.b2bOnly}
                                onChange={(e) => setAccess({ b2bOnly: e.target.checked })} />}
                        />
                    )}
                    <PdiRow
                        label={tr('cfg_proc_pos_code', 'Register new customers under POS')}
                        help={tr('cfg_proc_pos_code_help', 'The point-of-sale identifier in your system that new customer accounts are linked to.')}
                        control={
                            <TextField select size="small"
                                value={config.access.posCode}
                                onChange={(e) => setAccess({ posCode: e.target.value })}
                                sx={{ minWidth: 260 }}
                            >
                                {posListWithCurrent.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_guest_id', 'Guest customer ID')}
                        help={tr('cfg_proc_guest_id_help', 'Fill in a customer ID to allow visitors to place orders without creating an account. Their orders will be linked to this generic customer record. Leave empty to require account creation for all orders.')}
                        align="top"
                        control={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                                <TextField size="small"
                                    value={config.access.guestCustomerId}
                                    onChange={(e) => setAccess({ guestCustomerId: e.target.value })}
                                    error={custCheck.status === 'invalid'}
                                    sx={{ width: 160 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {custCheck.status === 'checking' && <CircularProgress size={16} thickness={5} />}
                                                {custCheck.status === 'valid'    && <CheckCircleIcon fontSize="small" color="success" />}
                                                {custCheck.status === 'invalid'  && <ErrorOutlineIcon fontSize="small" color="error" />}
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {custCheck.status === 'valid' && (
                                    <Typography variant="caption" sx={{ color: 'success.main', maxWidth: 220, textAlign: 'right', lineHeight: 1.2 }}>
                                        {custCheck.name}
                                    </Typography>
                                )}
                                {custCheck.status === 'invalid' && (
                                    <Typography variant="caption" sx={{ color: 'error.main' }}>
                                        {tr('cfg_proc_guest_id_invalid', 'No customer found with this ID')}
                                    </Typography>
                                )}
                                {custCheck.status === 'error' && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {tr('cfg_proc_guest_id_error', 'Could not verify — check connection')}
                                    </Typography>
                                )}
                            </Box>
                        }
                    />
                    <Box sx={{ opacity: guestEnabled ? 1 : 0.5, pointerEvents: guestEnabled ? 'auto' : 'none' }}>
                        <PdiRow
                            indent
                            label={tr('cfg_proc_guest_pickup', 'Allow guest pickup orders')}
                            help={tr('cfg_proc_guest_pickup_help', "When off, guests can still order but only for shipping — pickup orders require an account. When on, guests can order both ways. Has no effect if the guest customer ID above is empty.")}
                            control={<Switch color="success" size="small"
                                checked={!!config.access.allowGuestPickup}
                                onChange={(e) => setAccess({ allowGuestPickup: e.target.checked })} />}
                        />
                    </Box>
                </Stack>
            </ConfigCard>

            {/* Warehouse */}
            <ConfigCard
                title={tr('cfg_proc_warehouse_title', 'Warehouse')}
                description={tr('cfg_proc_warehouse_desc', 'The warehouse where webshop orders are created in your inventory system.')}
                theme={theme}
            >
                <PdiRow
                    label={tr('cfg_proc_warehouse_label', 'Order warehouse')}
                    help={tr('cfg_proc_warehouse_help', 'Pick one warehouse — all webshop orders are registered here')}
                    control={
                        <TextField select size="small"
                            value={config.warehouse}
                            onChange={(e) => setConfig(p => ({ ...p, warehouse: e.target.value }))}
                            sx={{ minWidth: 220 }}
                        >
                            {whListWithCurrent.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                    }
                />
            </ConfigCard>

            {/* Discount type & vouchers — table */}
            <ConfigCard
                title={tr('cfg_proc_disc_title', 'Discount type & vouchers')}
                description={tr('cfg_proc_disc_desc', 'Which discount types and vouchers are accepted, and which are added to the order confirmation email. Each row represents a discount type defined in your back office.')}
                theme={theme}
            >
                <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    {/* Header */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 100px 140px 120px', alignItems: 'center', gap: 2, mb: 1, px: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                            {tr('cfg_proc_disc_col_type', 'DISCOUNT TYPE')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_disc_col_enabled', 'ENABLED')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_disc_col_payment', 'ACCEPT AS PAYMENT')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_disc_col_email', 'SHOW IN EMAIL')}
                        </Typography>
                    </Box>
                    <Stack spacing={0.5}>
                        {DISCOUNT_TYPES.map(d => {
                            const row = config.discountTypes[d.code] || {};
                            const enabled = !!row.enabled;
                            const dim     = !enabled;
                            return (
                                <Box key={d.code} sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 100px 140px 120px',
                                    alignItems: 'center', gap: 2,
                                    px: 1, py: 0.75,
                                    borderRadius: 1,
                                    backgroundColor: '#fff',
                                    border: '1px solid', borderColor: 'divider',
                                    opacity: dim ? 0.6 : 1,
                                }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tr(d.labelKey, d.label)}</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {tr('cfg_proc_disc_code', 'Code')}: {d.code}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={enabled} onChange={(e) => setDiscount(d.code, 'enabled', e.target.checked)} />
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={!!row.acceptAsPayment} onChange={(e) => setDiscount(d.code, 'acceptAsPayment', e.target.checked)} />
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={!!row.showInEmail} onChange={(e) => setDiscount(d.code, 'showInEmail', e.target.checked)} />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>

                <PdiRow
                    label={tr('cfg_proc_disc_barcode', 'Discount barcode')}
                    help={tr('cfg_proc_disc_barcode_help', 'The internal item barcode used to register general discounts on orders.')}
                    control={
                        <TextField size="small"
                            value={config.discountBarcode || ''}
                            onChange={(e) => setConfig(p => ({ ...p, discountBarcode: e.target.value }))}
                            sx={{ width: 220 }}
                        />
                    }
                />
            </ConfigCard>

            {/* Cart behavior */}
            <ConfigCard
                title={tr('cfg_proc_cart_title', 'Cart behavior')}
                description={tr('cfg_proc_cart_desc', 'How the shopping cart works for visitors.')}
                theme={theme}
            >
                <Stack spacing={1.25} divider={<Divider />}>
                    <PdiRow
                        label={tr('cfg_proc_cart_codes', 'Allow discount codes in cart')}
                        help={tr('cfg_proc_cart_codes_help', 'Visitors can enter a coupon or voucher code at checkout')}
                        control={<Switch color="success" size="small"
                            checked={!!config.cart.allowDiscountCodes}
                            onChange={(e) => setCart({ allowDiscountCodes: e.target.checked })} />}
                    />
                    <Box sx={{ opacity: config.cart.allowDiscountCodes ? 1 : 0.5, pointerEvents: config.cart.allowDiscountCodes ? 'auto' : 'none' }}>
                        <PdiRow
                            indent
                            label={tr('cfg_proc_cart_negative', 'Allow vouchers to exceed the order total')}
                            help={tr('cfg_proc_cart_negative_help', 'When on, a voucher/cheque can still be used even if it would make the cart total negative. Most shops leave this off.')}
                            control={<Switch color="success" size="small"
                                checked={!!config.cart.allowNegativeTotals}
                                onChange={(e) => setCart({ allowNegativeTotals: e.target.checked })} />}
                        />
                    </Box>
                    <PdiRow
                        label={tr('cfg_proc_cart_limit_qty', 'Limit quantity per item')}
                        help={tr('cfg_proc_cart_limit_qty_help', "When on, a customer can't add more than the limit per item. When off, the limit equals the available stock.")}
                        control={<Switch color="success" size="small"
                            checked={!!config.cart.limitQuantityPerItem}
                            onChange={(e) => setCart({ limitQuantityPerItem: e.target.checked })} />}
                    />
                    <Box sx={{ opacity: config.cart.limitQuantityPerItem ? 1 : 0.5, pointerEvents: config.cart.limitQuantityPerItem ? 'auto' : 'none' }}>
                        <PdiRow
                            indent
                            label={tr('cfg_proc_cart_max_qty', 'Maximum quantity per item')}
                            control={
                                <TextField type="number" size="small"
                                    value={config.cart.maxQuantityPerItem}
                                    onChange={(e) => setCart({ maxQuantityPerItem: Math.max(1, parseInt(e.target.value) || 1) })}
                                    InputProps={{ endAdornment: <InputAdornment position="end" sx={{ whiteSpace: 'nowrap', '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>{tr('cfg_proc_cart_items', 'items')}</InputAdornment> }}
                                    sx={{ width: 180 }}
                                />
                            }
                        />
                    </Box>
                    <PdiRow
                        label={tr('cfg_proc_cart_max_unique', 'Maximum unique items in cart')}
                        help={tr('cfg_proc_cart_max_unique_help', 'How many different products a single cart can contain')}
                        control={
                            <TextField type="number" size="small"
                                value={config.cart.maxUniqueItemsInCart}
                                onChange={(e) => setCart({ maxUniqueItemsInCart: Math.max(1, parseInt(e.target.value) || 1) })}
                                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ whiteSpace: 'nowrap', '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>{tr('cfg_proc_cart_unique_items', 'unique items')}</InputAdornment> }}
                                sx={{ width: 220 }}
                            />
                        }
                    />
                </Stack>
            </ConfigCard>

            {/* Registration form */}
            <ConfigCard
                title={tr('cfg_proc_reg_title', 'Registration form')}
                description={tr('cfg_proc_reg_desc', 'What information visitors are asked for when registering or visiting their account page.')}
                theme={theme}
            >
                <Stack spacing={1.25} divider={<Divider />}>
                    <PdiRow
                        label={tr('cfg_proc_reg_phone', 'Phone number is required')}
                        help={tr('cfg_proc_reg_phone_help', 'Visitors must provide a phone number when registering')}
                        control={<Switch color="success" size="small"
                            checked={!!config.registration.phoneRequired}
                            onChange={(e) => setRegistration({ phoneRequired: e.target.checked })} />}
                    />
                    <PdiRow
                        label={tr('cfg_proc_reg_salutation', 'Default salutation')}
                        help={tr('cfg_proc_reg_salutation_help', "Pre-fill this salutation if a customer doesn't pick one. Leave blank to require a choice.")}
                        control={
                            <TextField select size="small"
                                value={config.registration.defaultSalutation}
                                onChange={(e) => setRegistration({ defaultSalutation: e.target.value })}
                                sx={{ minWidth: 200 }}
                            >
                                {salListWithCurrent.map(o => <MenuItem key={o.value || 'none'} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_reg_country', 'Default country')}
                        help={tr('cfg_proc_reg_country_help', 'Pre-fill this country on registration forms. Leave blank to require a choice.')}
                        control={
                            <TextField select size="small"
                                value={config.registration.defaultCountry}
                                onChange={(e) => setRegistration({ defaultCountry: e.target.value })}
                                sx={{ minWidth: 200 }}
                            >
                                <MenuItem value="">— {tr('cfg_proc_reg_country_none', 'require choice')} —</MenuItem>
                                {countryListWithCurrent.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </TextField>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_reg_history', 'Show purchase history on account page')}
                        help={tr('cfg_proc_reg_history_help', 'Customers can see their past orders when logged in')}
                        control={<Switch color="success" size="small"
                            checked={!!config.registration.showPurchaseHistory}
                            onChange={(e) => setRegistration({ showPurchaseHistory: e.target.checked })} />}
                    />
                    <PdiRow
                        label={tr('cfg_proc_reg_disc', 'Show current customer discount on account page')}
                        help={tr('cfg_proc_reg_disc_help', 'Customers see the discount tier they currently qualify for')}
                        control={<Switch color="success" size="small"
                            checked={!!config.registration.showCustomerDiscount}
                            onChange={(e) => setRegistration({ showCustomerDiscount: e.target.checked })} />}
                    />
                </Stack>
            </ConfigCard>

            {/* External-stock indicator */}
            <ConfigCard
                title={tr('cfg_proc_extstock_title', 'External-stock indicator')}
                description={tr('cfg_proc_extstock_desc', 'Icon shown on products that are out of stock locally but can still be ordered from an external supplier. These orders may take longer to deliver.')}
                theme={theme}
            >
                <PdiRow
                    label={tr('cfg_proc_extstock_icon', 'Indicator icon')}
                    help={tr('cfg_proc_extstock_icon_help', 'Click to browse icons. Use the search to filter.')}
                    align="top"
                    control={
                        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5 }}>
                            <IconPicker
                                value={config.externalStock.iconName}
                                color={config.externalStock.iconColor}
                                onChange={(name) => setExternal({ iconName: name })}
                                label={tr('cfg_proc_extstock_selected', 'Selected icon')}
                                placeholderLabel={tr('cfg_proc_extstock_pick', 'Pick icon')}
                            />
                            {/* Color picker pill — mirrors the IconPicker's structure
                                (label + control on the left, text on the right) so
                                the two sit at the same height and baseline. */}
                            <Box component="label" sx={{
                                display: 'flex', alignItems: 'center', gap: 1.25,
                                p: 1, borderRadius: 1.5,
                                border: '1px solid', borderColor: 'divider',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                minWidth: 140,
                                transition: 'all 0.15s',
                                '&:hover': { borderColor: 'primary.main' },
                            }}>
                                <Box sx={{
                                    position: 'relative',
                                    width: 36, height: 36, borderRadius: 1,
                                    border: '1px solid rgba(0,0,0,0.18)',
                                    backgroundColor: config.externalStock.iconColor,
                                    flexShrink: 0,
                                }}>
                                    <input
                                        type="color"
                                        value={config.externalStock.iconColor}
                                        onChange={(e) => setExternal({ iconColor: e.target.value })}
                                        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                                        {tr('cfg_proc_extstock_color', 'Color')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', fontFamily: 'monospace' }}>
                                        {(config.externalStock.iconColor || '').toUpperCase()}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    }
                />
            </ConfigCard>

            {/* Ticket printing — table */}
            <ConfigCard
                title={tr('cfg_proc_tickets_title', 'Ticket printing')}
                description={tr('cfg_proc_tickets_desc', 'Which tickets get printed when an order arrives. Pickup orders and shipped orders can be configured separately.')}
                theme={theme}
            >
                <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    {/* Header */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 160px 160px', alignItems: 'center', gap: 2, mb: 1, px: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                            {tr('cfg_proc_tickets_col_type', 'TICKET TYPE')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_tickets_col_pickup', 'PICKUP ORDERS')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                            {tr('cfg_proc_tickets_col_shipped', 'SHIPPED ORDERS')}
                        </Typography>
                    </Box>
                    <Stack spacing={0.5}>
                        {TICKET_TYPES.map(tk => {
                            const row = config.ticketPrinting[tk.key] || {};
                            return (
                                <Box key={tk.key} sx={{
                                    display: 'grid', gridTemplateColumns: '1fr 160px 160px', alignItems: 'center', gap: 2,
                                    px: 1, py: 0.75, borderRadius: 1, backgroundColor: '#fff',
                                    border: '1px solid', borderColor: 'divider',
                                }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tk.label}</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tk.help}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={!!row.pickup}  onChange={(e) => setTicket(tk.key, 'pickup',  e.target.checked)} />
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Switch color="success" size="small" checked={!!row.shipped} onChange={(e) => setTicket(tk.key, 'shipped', e.target.checked)} />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </ConfigCard>
        </Stack>
    );
};
GeneralPanel.propTypes = { config: PropTypes.object, setConfig: PropTypes.func, setSection: PropTypes.func, posOptions: PropTypes.array, warehouseOptions: PropTypes.array, salutationOptions: PropTypes.array, countryOptions: PropTypes.array, validateCustomerId: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// PAYMENTS panel
// ─────────────────────────────────────────────────────────────────────────
const PAYMENT_LOCATIONS = [
    { value: 'inStore',         icon: <HomeOutlinedIcon />,       i18nKey: 'cfg_proc_pay_in_store',       fallback: 'In-store only',          descKey: 'cfg_proc_pay_in_store_desc',       descFallback: 'Pay in person when picking up' },
    { value: 'onlineAndStore',  icon: <CreditCardOutlinedIcon />, i18nKey: 'cfg_proc_pay_online_store',   fallback: 'Online & in-store',      descKey: 'cfg_proc_pay_online_store_desc',   descFallback: 'Customer chooses at checkout' },
    { value: 'onlineOnly',      icon: <StorefrontOutlinedIcon />, i18nKey: 'cfg_proc_pay_online_only',    fallback: 'Online only',            descKey: 'cfg_proc_pay_online_only_desc',    descFallback: 'All payments through the online provider' },
    { value: 'noCheckout',      icon: <BlockIcon />,              i18nKey: 'cfg_proc_pay_no_checkout',    fallback: 'No checkout possible',   descKey: 'cfg_proc_pay_no_checkout_desc',    descFallback: 'Catalogue / lookbook only — no orders' },
];

const PaymentsPanel = ({ value = {}, onChange, providerAvailability = {}, typeOptions = [], receiptPos = {}, onReceiptPosChange, posOptions = [], theme, tr }) => {
    // Payment types come from the POS kasvars (kas_betaal1..9) via the parent;
    // fall back to generic Type 1-9, and keep the saved value selectable.
    const ptBase = (Array.isArray(typeOptions) && typeOptions.length)
        ? typeOptions
        : ['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(n => ({ value: n, label: `Type ${n}` }));
    const ptList = (value.paymentType && !ptBase.some(o => o.value === String(value.paymentType)))
        ? [...ptBase, { value: String(value.paymentType), label: `Type ${value.paymentType}` }]
        : ptBase;
    // Kasticket (receipt) POS-id dropdowns — same POS pool as the General
    // panel's "register customers under POS"; keep saved value selectable.
    const posList = (Array.isArray(posOptions) && posOptions.length) ? posOptions : POS_OPTIONS;
    const keepPosCurrent = (val) => (val && !posList.some(o => o.value === val))
        ? [{ value: val, label: val }, ...posList]
        : posList;
    const receiptProcessList = keepPosCurrent(receiptPos?.process);
    const receiptPrintList   = keepPosCurrent(receiptPos?.print);
    const setReceiptPos = (patch) => { if (typeof onReceiptPosChange === 'function') onReceiptPosChange(patch); };
    return (
    <Stack spacing={2}>
        {/* Kasticket (receipt) POS IDs — at the very top of Payments (moved from
            General). 'Processing of the receipt' also drives the payment types. */}
        <ConfigCard
            anchor="cfg-card-proc-receipt-pos"
            title={tr('cfg_proc_receipt_pos_title', 'Receipt POS IDs')}
            description={tr('cfg_proc_receipt_pos_desc', 'Configure which POS IDs are used for the following actions.')}
            theme={theme}
        >
            <Stack spacing={1.25} divider={<Divider />}>
                <PdiRow
                    label={tr('cfg_proc_receipt_pos_process', 'Processing of the receipt')}
                    help={tr('cfg_proc_receipt_pos_process_help', 'The POS the receipt is processed on. Also drives which payment types are available — can differ per store.')}
                    control={
                        <TextField select size="small"
                            value={receiptPos?.process ?? ''}
                            onChange={(e) => setReceiptPos({ process: e.target.value })}
                            sx={{ minWidth: 260 }}
                        >
                            {receiptProcessList.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                    }
                />
                <PdiRow
                    label={tr('cfg_proc_receipt_pos_print', 'Printing the receipt')}
                    help={tr('cfg_proc_receipt_pos_print_help', 'The POS the receipt message is printed on.')}
                    control={
                        <TextField select size="small"
                            value={receiptPos?.print ?? ''}
                            onChange={(e) => setReceiptPos({ print: e.target.value })}
                            sx={{ minWidth: 260 }}
                        >
                            {receiptPrintList.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </TextField>
                    }
                />
            </Stack>
        </ConfigCard>
        <ConfigCard
            title={tr('cfg_proc_pay_title', 'Payment options')}
            description={tr('cfg_proc_pay_desc', 'Where customers can pay for their orders.')}
            theme={theme}
        >
            <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {tr('cfg_proc_pay_where', 'Where customers can pay')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                    {tr('cfg_proc_pay_where_help', 'Pick one. Affects which payment options appear at checkout.')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                    {PAYMENT_LOCATIONS.map(opt => {
                        const sel = value.location === opt.value;
                        return (
                            <ButtonBase
                                key={opt.value}
                                onClick={() => onChange({ location: opt.value })}
                                sx={{
                                    flexDirection: 'column', alignItems: 'flex-start', gap: 1,
                                    p: 1.75, borderRadius: 1.5,
                                    border: '1.5px solid',
                                    borderColor: sel ? 'primary.main' : 'divider',
                                    backgroundColor: sel ? 'rgba(25,118,210,0.08)' : '#fff',
                                    color: sel ? 'primary.main' : 'text.primary',
                                    textAlign: 'left',
                                    minHeight: 120,
                                }}
                            >
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: 1,
                                    backgroundColor: sel ? 'primary.main' : 'rgba(0,0,0,0.04)',
                                    color: sel ? '#fff' : 'text.secondary',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>{opt.icon}</Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {tr(opt.i18nKey, opt.fallback)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: sel ? 'primary.main' : 'text.secondary', lineHeight: 1.3 }}>
                                    {tr(opt.descKey, opt.descFallback)}
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Box>
            </Box>

            <Divider />

            <PdiRow
                label={tr('cfg_proc_pay_invoice', 'Allow customers to request an invoice')}
                help={tr('cfg_proc_pay_invoice_help', 'Adds a checkbox at checkout where customers can ask for an invoice. Useful for B2B-like behaviour even on B2C shops.')}
                control={<Switch color="success" size="small"
                    checked={!!value.allowInvoice}
                    onChange={(e) => onChange({ allowInvoice: e.target.checked })} />}
            />

            {/* Online payment provider (PAYMENT_PROVIDER) — HIDDEN for now per
                the user (2026-06-04): MultiSafePay is the only option today, so
                the select adds nothing. Kept in place (not deleted) so it can
                come straight back when a second provider lands; the wiring
                (PAYMENT_PROVIDER load/save + providerAvailability) stays intact. */}
            {false && (
            <PdiRow
                label={tr('cfg_proc_pay_provider', 'Online payment provider')}
                help={tr('cfg_proc_pay_provider_help', 'Which provider processes online payments.')}
                align="top"
                control={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <TextField select size="small"
                            value={value.provider || 'msp'}
                            onChange={(e) => onChange({ provider: e.target.value })}
                            sx={{ minWidth: 220 }}
                        >
                            <MenuItem value="msp" disabled={providerAvailability.msp === false}>MultiSafePay</MenuItem>
                        </TextField>
                        <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 260, textAlign: 'right', lineHeight: 1.3 }}>
                            {tr('cfg_proc_pay_provider_setup', 'If the provider is greyed out, add its (valid) account details in Connections first.')}
                        </Typography>
                    </Box>
                }
            />
            )}

            <PdiRow
                label={tr('cfg_proc_pay_type', 'Payment type')}
                help={tr('cfg_proc_pay_type_help', 'The back-office payment-type code online payments are booked on.')}
                control={
                    <TextField select size="small"
                        value={String(value.paymentType || '1')}
                        onChange={(e) => onChange({ paymentType: e.target.value })}
                        sx={{ minWidth: 200 }}
                    >
                        {ptList.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>
                }
            />
        </ConfigCard>
    </Stack>
    );
};
PaymentsPanel.propTypes = { value: PropTypes.object, onChange: PropTypes.func, providerAvailability: PropTypes.object, typeOptions: PropTypes.array, receiptPos: PropTypes.object, onReceiptPosChange: PropTypes.func, posOptions: PropTypes.array, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// SHIPPING & PICKUP panel
// ─────────────────────────────────────────────────────────────────────────
const DELIVERY_OPTIONS = [
    { value: 'pickupOnly',         icon: <HomeOutlinedIcon />,        i18nKey: 'cfg_proc_ship_pickup_only',  fallback: 'Pickup only',     descKey: 'cfg_proc_ship_pickup_only_desc',  descFallback: 'Customer collects from store'    },
    { value: 'shippingOnly',       icon: <LocalShippingOutlinedIcon />,i18nKey: 'cfg_proc_ship_ship_only',   fallback: 'Shipping only',   descKey: 'cfg_proc_ship_ship_only_desc',    descFallback: 'Always shipped to customer'      },
    { value: 'pickupAndShipping',  icon: <HandshakeOutlinedIcon />,   i18nKey: 'cfg_proc_ship_both',         fallback: 'Pickup & shipping', descKey: 'cfg_proc_ship_both_desc',       descFallback: 'Customer chooses at checkout'    },
];

const ShippingPanel = ({ value = {}, onChange, stores = [], onStoresChange, onApplyCountryPreset, posOptions = [], theme, tr }) => {
    const externalEnabled = !!value.externalStockEnabled;
    // Pickup is unavailable when the shop only ships orders.
    const pickupDisabled = value.method === 'shippingOnly';
    // Per-store pickup is the store's `allow_pickup` flag (shared with the Stores
    // editor). Toggling here updates the same stores state, saved via PATCH /stores.
    const setStorePickup = (storeId, on) => {
        if (typeof onStoresChange !== 'function') return;
        onStoresChange(stores.map(st => String(st.id) === String(storeId) ? { ...st, allowPickup: on } : st));
    };
    // Per-store pickup POS ids (legacy data-manager: pos_id / pos_id_print).
    // Patch a single store field, shared stores state, saved via PATCH /stores.
    const setStoreField = (storeId, patch) => {
        if (typeof onStoresChange !== 'function') return;
        onStoresChange(stores.map(st => String(st.id) === String(storeId) ? { ...st, ...patch } : st));
    };
    // Same POS pool as the Payments "receipt POS IDs" / General "register under
    // POS" dropdowns. POS ids are 4-digit: first 2 = store code, last 2 = the
    // register. We persist ONLY the register per store (the store implies the
    // prefix), but the <select> works in full-id space so the right option
    // highlights. Helpers:
    //  - storePrefix:    the store's 2-digit code (= the POS prefix)
    //  - reg2:           normalise a stored value to its 2-digit register
    //                    (handles legacy 4-digit values + unpadded ones)
    //  - posOptsForStore: only THIS store's registers (POS code starts with the
    //                    prefix), keeping an unknown saved value selectable
    const posList = (Array.isArray(posOptions) && posOptions.length) ? posOptions : POS_OPTIONS;
    const storePrefix = (st) => String(st.id).padStart(2, '0');
    const reg2 = (val) => (val ? String(val).slice(-2).padStart(2, '0') : '');
    const posOptsForStore = (prefix, fullVal) => {
        let opts = posList.filter(o => String(o.value).slice(0, 2) === prefix);
        if (fullVal && !opts.some(o => o.value === fullVal)) opts = [{ value: fullVal, label: fullVal }, ...opts];
        return opts;
    };
    return (
        <Stack spacing={2}>
            <ConfigCard
                title={tr('cfg_proc_ship_title', 'Delivery options')}
                description={tr('cfg_proc_ship_desc', 'How customers can receive their orders.')}
                theme={theme}
            >
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tr('cfg_proc_ship_methods', 'Available delivery methods')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
                        {tr('cfg_proc_ship_methods_help', 'Pick one. The customer chooses at checkout if both are allowed.')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                        {DELIVERY_OPTIONS.map(opt => {
                            const sel = value.method === opt.value;
                            return (
                                <ButtonBase
                                    key={opt.value}
                                    onClick={() => onChange({ method: opt.value })}
                                    sx={{
                                        flexDirection: 'column', alignItems: 'flex-start', gap: 1,
                                        p: 1.75, borderRadius: 1.5,
                                        border: '1.5px solid',
                                        borderColor: sel ? 'primary.main' : 'divider',
                                        backgroundColor: sel ? 'rgba(25,118,210,0.08)' : '#fff',
                                        color: sel ? 'primary.main' : 'text.primary',
                                        textAlign: 'left',
                                        minHeight: 110,
                                    }}
                                >
                                    <Box sx={{
                                        width: 32, height: 32, borderRadius: 1,
                                        backgroundColor: sel ? 'primary.main' : 'rgba(0,0,0,0.04)',
                                        color: sel ? '#fff' : 'text.secondary',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>{opt.icon}</Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {tr(opt.i18nKey, opt.fallback)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: sel ? 'primary.main' : 'text.secondary', lineHeight: 1.3 }}>
                                        {tr(opt.descKey, opt.descFallback)}
                                    </Typography>
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>

                {!externalEnabled && (
                    <Alert severity="warning" icon={false}>
                        <Typography variant="body2">
                            {tr('cfg_proc_ship_ext_alert_pre', 'The setting below requires')}{' '}
                            <Box component="strong" sx={{ color: 'warning.dark' }}>
                                {tr('cfg_proc_ship_ext_alert_term', 'external stock')}
                            </Box>{' '}
                            {tr('cfg_proc_ship_ext_alert_post', "to be enabled in Data sync. It's currently disabled there, so this option is unavailable.")}
                        </Typography>
                    </Alert>
                )}

                <Box sx={{ opacity: externalEnabled ? 1 : 0.5, pointerEvents: externalEnabled ? 'auto' : 'none' }}>
                    <PdiRow
                        label={tr('cfg_proc_ship_route_external', 'Route external orders through the shop first')}
                        help={tr('cfg_proc_ship_route_external_help', 'When an order contains items from an external supplier, ship them to the shop before forwarding to the customer. Helps with quality control.')}
                        control={<Switch color="success" size="small"
                            checked={!!value.routeExternalThroughShop}
                            onChange={(e) => onChange({ routeExternalThroughShop: e.target.checked })} />}
                    />
                </Box>
            </ConfigCard>

            {/* Pickup locations — per-store toggle for whether customers can
                collect orders there. Hidden entirely when the shop ships only,
                since pickup isn't on offer and the section would just be a
                disabled box taking up space. */}
            {!pickupDisabled && (
            <ConfigCard
                anchor="cfg-card-proc-pickup-stores"
                title={tr('cfg_proc_ship_pickup_stores_title', 'Pickup locations')}
                description={tr('cfg_proc_ship_pickup_stores_desc', 'Pick which stores customers can collect their orders from. Stores not toggled on here will not appear as a pickup choice at checkout.')}
                theme={theme}
            >
                <Box>
                    {stores.length === 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                            {tr('cfg_proc_ship_pickup_no_stores', 'No stores loaded yet.')}
                        </Typography>
                    )}
                    <Stack spacing={0.5}>
                        {stores.map((s) => {
                            // Default to enabled unless the store is explicitly off
                            // (store.allow_pickup) — `=== false` not `!!`.
                            const enabled = s.allowPickup !== false;
                            return (
                                // Single compact row: store identity (flex) + the two
                                // per-store pickup POS selects + the pickup Switch — all
                                // inline. The POS selects are ALWAYS shown (the legacy
                                // data-manager kept them always editable too); they're
                                // small and labelled in-field, so the row stays tidy.
                                // Not a ButtonBase (it nests selects); the Switch toggles.
                                <Box
                                    key={s.id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                                        width: '100%',
                                        px: 1.25, py: 0.85,
                                        border: '1px solid',
                                        borderColor: enabled ? 'success.light' : 'divider',
                                        borderRadius: 1,
                                        backgroundColor: enabled ? 'rgba(34,197,94,0.06)' : '#fff',
                                        transition: 'all 0.12s',
                                    }}
                                >
                                    <Box sx={{
                                        width: 8, height: 8, borderRadius: '50%',
                                        backgroundColor: s.visible ? '#22C55E' : '#9CA3AF',
                                        flexShrink: 0,
                                    }} />
                                    <Box sx={{ flex: 1, minWidth: 150 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 700, color: 'text.disabled', mr: 0.75 }}>{s.id}</Box>
                                            {s.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                                            {s.street} · {s.zip} {s.city}
                                        </Typography>
                                    </Box>
                                    {/* Per-store pickup POS selects (pos_id = register
                                        order, pos_id_print = print order). Compact +
                                        in-field labels; full "(afhalen)" meaning on hover. */}
                                    {[
                                        { key: 'posId',      label: tr('cfg_proc_ship_pickup_pos_process_short', 'Register'), full: tr('cfg_proc_ship_pickup_pos_process', 'Register order POS (pickup)') },
                                        { key: 'posIdPrint', label: tr('cfg_proc_ship_pickup_pos_print_short',   'Print'),    full: tr('cfg_proc_ship_pickup_pos_print',   'Print order POS (pickup)') },
                                    ].map((f) => {
                                        // Reconstruct the full 4-digit id (store prefix +
                                        // saved register) to highlight the right option;
                                        // filter the list to this store's registers.
                                        const prefix = storePrefix(s);
                                        const selVal = reg2(s[f.key]) ? `${prefix}${reg2(s[f.key])}` : '';
                                        const opts   = posOptsForStore(prefix, selVal);
                                        return (
                                            <TextField key={f.key} select size="small"
                                                label={f.label}
                                                title={f.full}
                                                value={selVal}
                                                // Persist ONLY the last 2 digits (register).
                                                onChange={(e) => setStoreField(s.id, { [f.key]: e.target.value ? String(e.target.value).slice(-2) : '' })}
                                                sx={{ width: 220, flexShrink: 0, '& .MuiInputBase-root': { backgroundColor: '#fff' } }}
                                            >
                                                <MenuItem value=""><em>{tr('cfg_proc_ship_pickup_pos_none', 'None')}</em></MenuItem>
                                                {opts.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                            </TextField>
                                        );
                                    })}
                                    {/* Pickup state — kept next to the Switch so the
                                        row still reads as available / not at a glance.
                                        Fixed width + right-aligned so the varying label
                                        length ("Pickup available" vs "No pickup") doesn't
                                        shift the POS selects out of vertical alignment
                                        between rows. */}
                                    <Typography variant="caption" sx={{ color: enabled ? 'success.dark' : 'text.disabled', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap', width: 120, textAlign: 'right' }}>
                                        {enabled
                                            ? tr('cfg_proc_ship_pickup_yes', 'Pickup available')
                                            : tr('cfg_proc_ship_pickup_no',  'No pickup')}
                                    </Typography>
                                    <Switch
                                        color="success" size="small"
                                        checked={enabled}
                                        onChange={(e) => setStorePickup(s.id, e.target.checked)}
                                        sx={{ flexShrink: 0 }}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </ConfigCard>
            )}

            <ConfigCard
                title={tr('cfg_proc_ship_costs_title', 'Shipping costs')}
                description={tr('cfg_proc_ship_costs_desc', 'Default shipping costs. Set country-specific rates in the dedicated shipping rates page.')}
                theme={theme}
            >
                <Stack spacing={1.25} divider={<Divider />}>
                    <PdiRow
                        label={tr('cfg_proc_ship_default_cost', 'Default shipping cost')}
                        help={tr('cfg_proc_ship_default_cost_help', 'Charged on shipped orders, unless overridden by a country-specific rate')}
                        control={
                            <TextField type="number" size="small"
                                value={value.defaultCost ?? 0}
                                onChange={(e) => onChange({ defaultCost: Math.max(0, parseFloat(e.target.value) || 0) })}
                                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                sx={{ width: 130 }}
                            />
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_ship_default_pickup_cost', 'Default pickup cost')}
                        help={tr('cfg_proc_ship_default_pickup_cost_help', 'Charged when the customer picks up the order at a parcel pickup point')}
                        control={
                            <TextField type="number" size="small"
                                value={value.defaultPickupCost ?? 0}
                                onChange={(e) => onChange({ defaultPickupCost: Math.max(0, parseFloat(e.target.value) || 0) })}
                                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                sx={{ width: 130 }}
                            />
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_ship_free_threshold', 'Free shipping from')}
                        help={tr('cfg_proc_ship_free_threshold_help', 'Orders from this amount up get free shipping. Set to 0 to never offer free shipping.')}
                        control={
                            <TextField type="number" size="small"
                                value={value.freeFromAmount ?? 0}
                                onChange={(e) => onChange({ freeFromAmount: Math.max(0, parseFloat(e.target.value) || 0) })}
                                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                                sx={{ width: 130 }}
                            />
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_ship_country_preset', 'Default country settings')}
                        help={tr('cfg_proc_ship_country_preset_help', 'Apply a preset to which countries are enabled and which is preferred. This overwrites the visibility of ALL countries (managed in the Data tab).')}
                        align="top"
                        control={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField select size="small"
                                        value={value.countryPreset || 'default'}
                                        onChange={(e) => onChange({ countryPreset: e.target.value })}
                                        sx={{ minWidth: 260 }}
                                    >
                                        <MenuItem value="allOff">{tr('cfg_proc_ship_country_preset_all_off', 'Disable all countries')}</MenuItem>
                                        <MenuItem value="allOn">{tr('cfg_proc_ship_country_preset_all_on', 'Enable all countries (Pref. Belgium)')}</MenuItem>
                                        <MenuItem value="default">{tr('cfg_proc_ship_country_preset_default', 'Default')}</MenuItem>
                                        <MenuItem value="belgium">{tr('cfg_proc_ship_country_preset_belgium', 'Belgium')}</MenuItem>
                                        <MenuItem value="benelux">{tr('cfg_proc_ship_country_preset_benelux', 'Benelux (no preferred)')}</MenuItem>
                                        <MenuItem value="europe">{tr('cfg_proc_ship_country_preset_europe', 'Europe (Pref. Belgium)')}</MenuItem>
                                    </TextField>
                                    <Button variant="outlined" size="small"
                                        disabled={typeof onApplyCountryPreset !== 'function'}
                                        onClick={() => {
                                            const preset = value.countryPreset || 'default';
                                            if (window.confirm(tr('cfg_proc_ship_country_preset_confirm', 'This resets the visibility + preferred flag of ALL countries to the chosen preset. Per-country shipping rates are not affected. Continue?'))) {
                                                onApplyCountryPreset(preset);
                                            }
                                        }}
                                        sx={{ textTransform: 'none', borderRadius: 1.5 }}
                                    >
                                        {tr('cfg_proc_ship_country_preset_apply', 'Apply')}
                                    </Button>
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {tr('cfg_proc_ship_country_preset_immediate', 'Applies immediately (not via Save).')}
                                </Typography>
                            </Box>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_ship_country_rates', 'Country-specific shipping rates')}
                        help={tr('cfg_proc_ship_country_rates_help', 'Different rates and free-shipping thresholds per country are managed in the Data tab → Countries (columns "Shipping costs" / "Free shipping from"). Belgium uses the default cost above.')}
                        control={
                            <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 260, textAlign: 'right' }}>
                                {tr('cfg_proc_ship_country_rates_hint', 'Edit per-country rates in the Data tab.')}
                            </Typography>
                        }
                    />
                    <PdiRow
                        label={tr('cfg_proc_ship_cost_item', 'Shipping cost item')}
                        help={tr('cfg_proc_ship_cost_item_help', 'The internal item barcode used to register shipping costs on orders')}
                        control={
                            <TextField size="small"
                                value={value.shippingCostItem || ''}
                                onChange={(e) => onChange({ shippingCostItem: e.target.value })}
                                sx={{ width: 220 }}
                            />
                        }
                    />
                </Stack>
            </ConfigCard>
        </Stack>
    );
};
ShippingPanel.propTypes = { value: PropTypes.object, onChange: PropTypes.func, stores: PropTypes.array, onStoresChange: PropTypes.func, onApplyCountryPreset: PropTypes.func, posOptions: PropTypes.array, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// CUSTOMER FIELDS panel
// ─────────────────────────────────────────────────────────────────────────
// Customer code groups are the POS slots 01-10 (each usable once). The codes
// map to a back-office customer-code group; their per-value options are edited
// in the POS, not here.
const CUSTOMER_CODE_GROUP_CODES = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10'];
const MAX_CUSTOMER_FIELDS = CUSTOMER_CODE_GROUP_CODES.length;

const CustomerFieldsPanel = ({ value = [], onChange, theme, tr }) => {
    const remaining = MAX_CUSTOMER_FIELDS - value.length;
    const updateField = (idx, patch) => onChange(value.map((f, i) => i === idx ? { ...f, ...patch } : f));
    const updateLabel = (idx, lang, val) => updateField(idx, { labels: { ...(value[idx].labels || {}), [lang]: val } });
    const updateShowOn = (idx, key, val) => updateField(idx, { showOn: { ...(value[idx].showOn || {}), [key]: val } });
    const removeField = (idx) => onChange(value.filter((_, i) => i !== idx));
    // Codes already taken by OTHER rows (so each group is picked at most once).
    const codesUsedByOthers = (skipIdx) => new Set(value.filter((_, i) => i !== skipIdx).map(f => f.codeGroup).filter(Boolean));
    const addField = () => {
        if (value.length >= MAX_CUSTOMER_FIELDS) return;
        const taken = new Set(value.map(f => f.codeGroup).filter(Boolean));
        const nextCode = CUSTOMER_CODE_GROUP_CODES.find(c => !taken.has(c)) || '';
        onChange([...value, {
            id: null,
            codeGroup: nextCode,
            showOn: { registration: true, account: true, newsletter: false },
            labels: { nl: '', en: '', fr: '' },
            _new: true,
        }]);
    };

    return (
        <Stack spacing={2}>
            <ConfigCard
                title={tr('cfg_proc_cf_title', 'Custom customer fields')}
                description={tr('cfg_proc_cf_desc', 'Extra fields (up to 10) that show on registration, account, or newsletter forms. Each maps to a customer code group (01-10) in your back office; the values within a group are edited there.')}
                theme={theme}
            >
                <Stack spacing={1.5}>
                    {value.map((field, idx) => (
                        <Paper key={field.id ?? `new-${idx}`} variant="outlined" sx={{
                            p: 2, borderRadius: 2,
                            backgroundColor: 'rgba(0,0,0,0.02)',
                        }}>
                            {/* Field header — number badge + "Field N" + Slot xx */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{
                                        width: 24, height: 24, borderRadius: '50%',
                                        backgroundColor: 'rgba(25,118,210,0.08)',
                                        color: 'primary.main',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: '0.78rem',
                                    }}>{idx + 1}</Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {tr('cfg_proc_cf_field', 'Field')} {idx + 1}
                                    </Typography>
                                </Box>
                                {field.codeGroup ? (
                                    <Chip
                                        label={`${tr('cfg_proc_cf_code', 'Code')}: ${field.codeGroup}`}
                                        size="small"
                                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', backgroundColor: 'transparent', border: 'none' }}
                                    />
                                ) : null}
                            </Box>

                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                {/* Customer code group dropdown */}
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
                                        {tr('cfg_proc_cf_code_group', 'CUSTOMER CODE GROUP')}
                                    </Typography>
                                    <TextField select size="small" fullWidth
                                        value={field.codeGroup || ''}
                                        onChange={(e) => updateField(idx, { codeGroup: e.target.value })}
                                        sx={{ backgroundColor: '#fff' }}
                                    >
                                        <MenuItem value="">— {tr('cfg_proc_cf_select_group', 'select group')} —</MenuItem>
                                        {CUSTOMER_CODE_GROUP_CODES.map(c => <MenuItem key={c} value={c} disabled={codesUsedByOthers(idx).has(c)}>{c}</MenuItem>)}
                                    </TextField>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                        {tr('cfg_proc_cf_edit_in_office', 'Edit the available values in your back office')}
                                    </Typography>
                                </Box>

                                {/* Show on checkboxes */}
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
                                        {tr('cfg_proc_cf_show_on', 'SHOW THIS FIELD ON')}
                                    </Typography>
                                    <Stack spacing={0}>
                                        {[
                                            { key: 'registration', label: tr('cfg_proc_cf_show_reg',  'Registration screen')      },
                                            { key: 'account',      label: tr('cfg_proc_cf_show_acc',  'Customer account page')    },
                                            { key: 'newsletter',   label: tr('cfg_proc_cf_show_news', 'Newsletter signup pop-up') },
                                        ].map(opt => (
                                            <FormControlLabel
                                                key={opt.key}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={!!field.showOn?.[opt.key]}
                                                        onChange={(e) => updateShowOn(idx, opt.key, e.target.checked)}
                                                    />
                                                }
                                                label={<Typography variant="body2">{opt.label}</Typography>}
                                                sx={{ ml: -0.75 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>

                            {/* Label per language */}
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
                                    {tr('cfg_proc_cf_label', 'LABEL SHOWN TO CUSTOMERS')}
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                                    {[
                                        { code: 'nl', short: 'NL', label: tr('cfg_proc_cf_lang_nl', 'Dutch')   },
                                        { code: 'en', short: 'GB', label: tr('cfg_proc_cf_lang_en', 'English') },
                                        { code: 'fr', short: 'FR', label: tr('cfg_proc_cf_lang_fr', 'French')  },
                                    ].map(l => (
                                        <Box key={l.code} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, backgroundColor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1 }}>
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, fontSize: '0.65rem' }}>{l.short}</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 50 }}>{l.label}</Typography>
                                            <TextField
                                                size="small" variant="standard"
                                                value={field.labels?.[l.code] || ''}
                                                onChange={(e) => updateLabel(idx, l.code, e.target.value)}
                                                sx={{ flex: 1, '& .MuiInput-input': { fontSize: '0.85rem', py: 0.5 } }}
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined" color="error" size="small"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() => removeField(idx)}
                                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                                >
                                    {tr('cfg_proc_cf_remove', 'Remove field')}
                                </Button>
                            </Box>
                        </Paper>
                    ))}

                    {remaining > 0 && (
                        <ButtonBase
                            onClick={addField}
                            sx={{
                                width: '100%', py: 1.5,
                                border: '1.5px dashed', borderColor: 'divider',
                                borderRadius: 1.5,
                                color: 'text.secondary',
                                gap: 0.75,
                                '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                            }}
                        >
                            <AddIcon fontSize="small" />
                            <Typography variant="body2">
                                {tr('cfg_proc_cf_add', 'Add another field')} ({remaining} {tr('cfg_proc_cf_of', 'of')} {MAX_CUSTOMER_FIELDS} {tr('cfg_proc_cf_slots_remaining', 'slots remaining')})
                            </Typography>
                        </ButtonBase>
                    )}
                </Stack>
            </ConfigCard>
        </Stack>
    );
};
CustomerFieldsPanel.propTypes = { value: PropTypes.array, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

export default ProcessingTab;
